import {
  dimensionBadgeBase,
  dimensionBadgeStateColors,
} from '@src/components/common/dimensionBadgeStyles';
import {
  InlineRenameField,
  PENCIL_REVEAL,
} from '@src/components/common/InlineRenameField';
import { useViewedClips } from '@src/hooks/useViewedClips';
import { copyClipWithOptimisticUI } from '@src/services/clipCopyService';
import { deriveClipPublishState } from '@src/services/publish/clipCardPublishState';
import { composeReelSummary } from '@src/services/reel/reelStructureSummary';
import { VideoDeletionService } from '@src/services/VideoDeletionService';
import { isTempClipId } from '@src/services/WriteQueueService';
import {
  useContentStudioUI,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CommentsIcon from '@src/svgs/CommentsIcon';
import CopyIcon from '@src/svgs/CopyIcon';
import DownloadIcon from '@src/svgs/DownloadIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import ShareIcon from '@src/svgs/ShareIcon';
import { stripMentionTokens } from '@src/utility/commentMentions';
import { extractErrorInfo } from '@src/utility/error';
import {
  getLastActionEditor,
  getLastActionTimestamp,
} from '@src/utility/lastAction';
import { LogManager } from '@src/utility/Logger';
import { transcriptSnippet } from '@src/utility/transcriptSnippet';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css, keyframes } from 'styled-components';

import PencilIcon from '../../svgs/PencilIcon';
import RemoveIcon from '../../svgs/RemoveIcon';
import {
  CLIP_REVIEW_STATUSES,
  ClipReviewStatus,
  DEFAULT_CLIP_REVIEW_STATUS,
} from '../../types.ts/video';
import { formatDateTimeCompact } from '../../utility/dateFormat';
import { formatLanguageCaption } from '../../utility/languageCaption';
import { formatRelativeTime } from '../../utility/relativeTime';
import { getFormattedDurationForStoryVideo } from '../../utility/timeFormat';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import {
  downloadVideoClip,
  getThumbnailUrlAndAspectRatio,
} from '../../utility/videoClip';
import { AutosaveStatusIndicator } from '../common/AutosaveStatusIndicator';
import ClientReadyFlag from '../common/ClientReadyFlag';
import {
  ClipContextTooltip,
  DARK_TOOLTIP_STYLE,
} from '../common/ClipContextTooltip';
import { ClipTypeBadge } from '../common/ClipTypeBadge';
import { ConfirmModal } from '../common/ConfirmModal';
import DurationOverlayBadge from '../common/DurationOverlayBadge';
import Modal from '../common/Modal';
import { SelectionOrderBadge } from '../common/SelectionOrderBadge';
import { SpeakerFacePile } from '../common/SpeakerFacePile';
import { MODAL_TOOLTIP_STYLE } from '../common/tooltipStyles';
import { TranscriptExcerptPanel } from '../common/TranscriptExcerptPanel';
import ImpactScoreBadge from '../sidepanel/ImpactScoreBadge';
import SpinningLoading from '../SpinningLoading';
import CopyContentModal from '../stage/CopyContentModal';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import ClipCardSelect from './ClipCardSelect';
import { clipCardTag } from './clipCardTag';
import { ExportMenu } from './ExportMenu';
import { useClipAssignees } from './hooks/useClipAssignees';
import { usePublishRecordDownload } from './hooks/usePublishRecordDownload';
import LastPublishedInfo from './LastPublishedInfo';
import { reelClipRows } from './reelClipRows';
import { clipIdentifiedSpeakers } from './ReelEditor/clipPickerFilter';
import SocialShareStatus, { getSharedContents } from './SocialShareStatus';
import SocialStats from './SocialStats';
import { useOpenClipPreview } from './useOpenClipPreview';

import type {
  ClipMetadata,
  DatoSpeaker,
  OtherVideo,
  Video,
} from '../../types.ts/story';
import type { BadgeColor } from '../../utility/badgeColors';
import type { TooltipEntry } from '../common/ClipContextTooltip';
import type { ClipPublishRecordsLookup } from './hooks/useStoryClipPublishRecords';
import type { ClipCommentThreadSummary } from '@src/components/content-components/hooks/useClipCommentCounts';
import type { ClipCommentPreview } from '@src/types.ts/comment';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

/** Duration (ms) for the highlight animation on recently saved clips */
const HIGHLIGHT_DURATION_MS = 6000;

/** Status-dot colors for the clip review workflow status. */
const STATUS_DOT_COLOR: Record<ClipReviewStatus, string> = {
  [ClipReviewStatus.New]: 'var(--text-secondary)',
  [ClipReviewStatus.Working]: 'var(--badge-blue)',
  [ClipReviewStatus.NeedsReview]: 'var(--badge-orange)',
  [ClipReviewStatus.Approved]: 'var(--primary)',
};

const CLIP_STATUS_OPTIONS = CLIP_REVIEW_STATUSES.map(status => ({
  value: status,
  label: status,
  dotColor: STATUS_DOT_COLOR[status],
}));

const BADGE_TOOLTIP_STYLE: CSSProperties = {
  ...MODAL_TOOLTIP_STYLE,
  maxWidth: '260px',
};

function buildClipTooltipEntries(clipJson?: ClipMetadata): TooltipEntry[] {
  if (!clipJson) return [];
  const entries: TooltipEntry[] = [];

  // A reel names no prompt. `buildReelMetadata` copies `theme` and
  // `customPrompt` off the first clip that went into it, so showing them here
  // would present one contributor's prompt as the whole reel's — a reel built
  // by hand was not produced from a prompt at all (PLAT-11049).
  if (!clipJson.isReel) {
    if (clipJson.customPrompt) {
      entries.push({ label: 'Custom', value: clipJson.customPrompt });
    }
    if (clipJson.theme && clipJson.theme !== 'Custom') {
      entries.push({ label: 'Prompt', value: clipJson.theme });
    }
  }
  if (clipJson.suggestedTheme) {
    entries.push({ label: 'Topic', value: clipJson.suggestedTheme });
  }
  if (clipJson.speakerName) {
    entries.push({ label: 'Speaker', value: clipJson.speakerName });
  }

  return entries.filter(e => e.value);
}

/**
 * What a reel adds up to, for the Reel badge's hover: the one line the Reel
 * Editor states under its toolbar, so the grid answers "what is in this reel"
 * in the same words, without the creator having to open it.
 *
 * `composeReelSummary` rather than four rows of label and value: the panel and
 * the transcript already describe a reel with this exact string (PLAT-11253
 * DS8), and a third phrasing here is the thing that helper exists to prevent.
 *
 * `speakerCount` is passed in because the card has already resolved it against
 * the story's speakers — two labels can be one person.
 */
function reelSummaryLine(
  clipJson: ClipMetadata | undefined,
  speakerCount: number,
): string | undefined {
  if (!clipJson?.isReel) return undefined;
  return composeReelSummary({
    voiceCount: speakerCount,
    clipCount: clipJson.subClips?.length ?? 0,
    duration: clipJson.duration ?? 0,
    chapterCount: clipJson.reel?.chapters?.length ?? 0,
  });
}

const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

type VideoContent = OtherVideo & { autoPlay?: boolean };
type Props = {
  video: VideoContent;
  /** Session-local poster used until the story's thumbnails are processed. */
  localThumbnailUrl?: string;
  preferLocalThumbnail?: boolean;
  /** Total comments on this clip; renders the indicator badge when > 0. */
  commentCount?: number;
  /** Open/resolved thread breakdown; shown in the badge hover tooltip. */
  commentThreads?: ClipCommentThreadSummary | null;
  /** Most recent comment on this clip; shown in the badge hover tooltip. */
  latestComment?: ClipCommentPreview | null;
  /** Renders the multi-select marker (PLAT-11049). Omitted = today's card. */
  selectable?: boolean;
  selected?: boolean;
  /**
   * 1-based pick position shown in the marker; the selection order is the
   * reel's playback order (PLAT-11092), same as the Clip Producer results.
   */
  selectionOrder?: number | null;
  /**
   * Whether *any* clip in the grid is picked. Keeps every card's pick box up
   * once a selection exists, so picking a run does not mean hovering each card
   * in turn — the grid knows this, a card cannot (PLAT-11092).
   */
  selectionActive?: boolean;
  onToggleSelect?: (videoId: string) => void;
  /**
   * The story's `spk_X` labels resolved to people, for the face pile on the
   * thumbnail. Handed down by the grid, which resolves them once for the whole
   * story, rather than fetched per card. Omitted, or with nothing behind this
   * clip's labels, shows no pile at all.
   */
  speakersByLabel?: Map<string, DatoSpeaker>;
  /**
   * This clip's variants' publish records, looked up by Video record id. Handed
   * down by the grid, which fetches the story's records in one request rather
   * than one per card. Required because a clip published only in a translation
   * writes no legacy publish fields at all, so without records the card cannot
   * tell a dubbed publish from a draft (PLAT-11591).
   */
  publishRecordsFor: ClipPublishRecordsLookup;
};

const logger = LogManager.getLogger('component:VideoClipCard');

/**
 * The clip cards' own face-pile tooltip instance. Its own id rather than the
 * shared one, which `RecentContent` renders on the homepage — this grid does
 * not mount that, so the names would have nowhere to go.
 */
export const CLIP_CARD_SPEAKER_TOOLTIP_ID = 'clip-card-speaker-tooltip';

/**
 * Status line for the comment tooltip: "2 open · 1 resolved" (either side
 * omitted when zero); `null` when there are no threads to summarize.
 */
const threadSummaryLabel = (
  threads: ClipCommentThreadSummary | null,
): string | null => {
  if (!threads || threads.open + threads.resolved === 0) return null;
  if (threads.open === 0) return 'All resolved';
  const parts = [`${threads.open} open`];
  if (threads.resolved > 0) parts.push(`${threads.resolved} resolved`);
  return parts.join(' · ');
};

const VideoClipCard = observer((props: Props) => {
  const {
    video,
    localThumbnailUrl,
    preferLocalThumbnail = false,
    commentCount = 0,
    commentThreads = null,
    latestComment = null,
    selectable = false,
    selected = false,
    selectionOrder = null,
    selectionActive = false,
    onToggleSelect,
    speakersByLabel,
    publishRecordsFor,
  } = props;
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const contentStudioUI = useContentStudioUI();
  const { prefetch, open: openClipPreview } = useOpenClipPreview();
  const { isViewed, markViewed } = useViewedClips();
  const assignees = useClipAssignees();

  const assigneeOptions = useMemo(
    () => [
      // Always offer "Unassigned" so a clip can be cleared back to no assignee.
      { value: '', label: 'Unassigned' },
      ...assignees.map(a => ({
        value: a.email,
        label: a.name || a.email,
      })),
    ],
    [assignees],
  );

  const open = (clip: VideoContent, openInEditor: boolean) => {
    if (clip.id) markViewed(clip.id);
    openClipPreview(clip, openInEditor);
  };

  const [openRenameDropdown, toggleRenameDropdown] = useState<boolean>(false);
  const [exportOpen, setExportOpen] = useState<boolean>(false);
  const [contentToEdit, toggleContentToEditModal] = useState<{
    type: 'rename' | 'save_as';
  } | null>(null);
  const [loadingSpinnerText, setLoadingSpinnerText] = useState<
    string | undefined
  >();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  /** The inline-renamed title while its save is in flight; null otherwise. */
  const [pendingTitle, setPendingTitle] = useState<string | null>(null);
  /** Identifies the rename that currently owns `pendingTitle`. */
  const renameRequestRef = useRef(0);

  const renameRef = useRef<HTMLDivElement>(null);
  const {
    appEnableApprovedButton,
    enableSharing,
    contentStudioEnableBlockTempClipEditing,
    contentStudioEnableClipStatusAssignee,
    enableTranslationReviewerMode,
  } = useFlagsCombination(userIdentity.currentRole);

  useOutsideAlerter(renameRef, () => {
    toggleRenameDropdown(false);
  });

  const deletionService = useMemo(
    () => new VideoDeletionService({ videoCreator }),
    [videoCreator],
  );

  /**
   * Delete the video clip and all its associated videos using VideoDeletionService
   */
  async function deleteVideoClip() {
    const mainVideoId = video.id;
    if (!mainVideoId) {
      logger.warn('Cannot delete video: missing video ID');
      return;
    }

    const associatedVideos = video.associatedVideos || [];
    const allVideoIdsToDelete = [
      mainVideoId,
      ...associatedVideos.map(av => av.id).filter((id): id is string => !!id),
    ];

    // Get all other videos for calculating next video to select
    const story = videoCreator.story;
    const allVideoOptions = (story?.otherVideos || [])
      .filter((v): v is typeof v & { id: string } => !!v.id)
      .map(v => ({ id: v.id }));

    const result = await deletionService.deleteSelectedVideos(
      allVideoIdsToDelete,
      mainVideoId,
      associatedVideos,
      allVideoOptions,
    );

    if (!result.ok) {
      logger.error('Failed to delete video clip', {
        videoId: mainVideoId,
        error: result.error,
      });
    }
  }

  const duration = getFormattedDurationForStoryVideo(video);
  const currentStory = videoCreator.story;
  const clips = [video, ...(video.associatedVideos || [])];
  const shareableClip = clips.find(clip => clip.shareableImageId);

  const { thumbnailUrl, aspectRatio } = getThumbnailUrlAndAspectRatio(
    video,
    shareableClip,
    currentStory,
    shareableClip?.aspectRatio || video.aspectRatio,
  );
  const displayedThumbnailUrl =
    preferLocalThumbnail && video.clipJson
      ? localThumbnailUrl
      : localThumbnailUrl || thumbnailUrl;

  const shouldFillImage = () => {
    if (!shareableClip) return true;
    return !!video.shareableImageId;
  };
  const fillImage = shouldFillImage();

  // Derived in the card, not handed down: the card is an `observer`, so reading
  // the variants' `videoFilePrimary` / `lastPublishJson` here keeps the badge
  // reactive to the in-memory patch a finishing canonical publish applies
  // (`applyRenderingStatusToNode`). With no records for this clip the union
  // collapses to exactly the legacy fields, i.e. the pre-PLAT-11591 behaviour.
  const publishState = deriveClipPublishState(
    video,
    publishRecordsFor,
    videoCreator.story?.originalVideoLanguage || 'en',
  );
  const dimensions = publishState.formats;
  const { download: downloadRecord } = usePublishRecordDownload();

  /**
   * Download an aspect ratio's published render. A translated publish never
   * reaches the Video record's `videoFilePrimary`, which `downloadVideoClip`
   * requires — so it resolves through its `PublishRecord` instead, the same
   * path the clip preview's Downloads tab uses. Both branches must stop the
   * click reaching the card, which otherwise opens the preview.
   */
  const handleDownloadFormat = async (
    e: ReactMouseEvent<HTMLElement>,
    format: (typeof dimensions)[number],
  ) => {
    if (format.record) {
      e.preventDefault();
      e.stopPropagation();
      await downloadRecord(format.record, video.title || undefined);
      return;
    }
    await downloadVideoClip(e, video, currentStory, format.aspectRatio);
  };

  /**
   * "Download 16:9", or "Download 16:9 (Ukrainian)" when the render that would
   * come down is a translation — the card must not offer a dub as if it were
   * the original.
   */
  const downloadLabel = (format: (typeof dimensions)[number]) =>
    `Download ${format.aspectRatio}${
      format.record ? ` (${formatLanguageCaption(format.record.language)})` : ''
    }`;

  const renderPublishInfo = () => {
    // A dubbed publish is described by its record; `latest` already ranked it
    // against any legacy publish. Which language shipped is deliberately *not*
    // named here: the card is too narrow for it (the line ran off the card), and
    // the aspect-ratio badge directly above already carries the language in its
    // tooltip and its download label.
    const latest = publishState.latest;
    if (latest) {
      return <LastPublishedInfo lastPublishInfo={[latest.info]} />;
    }

    // For drafts, show last saved info from lastActionJson
    const editor = getLastActionEditor(video);
    const timestamp = getLastActionTimestamp(video);
    if (editor && timestamp > 0) {
      const savedText = `Saved ${formatDateTimeCompact(timestamp)} by ${editor}`;
      return <DraftInfo title={savedText}>{savedText}</DraftInfo>;
    }

    // Fallback for clips with no action info
    return <DraftInfo />;
  };

  const isRecentlySaved = video.id
    ? contentStudioUI.isRecentlySavedClip(video.id)
    : false;
  const [isHighlighted, setIsHighlighted] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isRecentlySaved) return;
    // Scroll to the first recently saved clip and highlight
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setIsHighlighted(true);
    const timer = setTimeout(() => {
      setIsHighlighted(false);
      if (video.id) {
        contentStudioUI.dismissClipHighlight(video.id);
      }
    }, HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isRecentlySaved]);

  const sharedContents = getSharedContents(video._allReferencingSharedContents);
  const countOfTrue = Object.values(sharedContents).filter(Boolean).length;
  const isSaving = isTempClipId(video.id);
  const isNew = !!video.id && !isViewed(video.id);
  const isEditingBlocked = contentStudioEnableBlockTempClipEditing && isSaving;
  const isActiveClip = videoCreator.videoClipPreview?.id === video.id;
  const selectableId = video.id;
  const allVideoIds = useMemo(() => {
    const ids = [
      video.id,
      ...(video.associatedVideos || []).map(v => v.id),
    ].filter(Boolean) as string[];
    return ids;
  }, [video.id, video.associatedVideos]);
  const isPublishing = allVideoIds.some(id =>
    videoCreator.renderingVideoIds.includes(id),
  );
  const clipTag = clipCardTag(video.clipJson);

  // PLAT-11249 — what this card *is*, said by the same identity badge the
  // clip preview's title row carries. The reel's old ThemeTag said "Reel" for
  // the same reason (a reel must not wear one contributor's prompt), so with
  // the badge present it stands down — saying it twice marks nothing.
  const isReelCard = !!video.clipJson?.isReel;
  const themeTag = isReelCard ? null : clipTag;

  // The people this clip records, resolved to speakers of the story and
  // deduplicated: two labels can be one person once speakers are combined, and
  // a reel contributes one label per passage. Labels with nobody behind them
  // drop out, which is what lets the pile be absent rather than anonymous.
  const cardSpeakers = useMemo(
    () => clipIdentifiedSpeakers(video, speakersByLabel),
    [video, speakersByLabel],
  );
  // Undefined on a clip, which is also what withholds the badge's tooltip:
  // "Clip" has nothing further to say about itself.
  const reelSummary = reelSummaryLine(video.clipJson, cardSpeakers.length);
  // Memoized because the card re-renders on hover/store churn and the source
  // transcript string can be large.
  const snippet = useMemo(
    () => transcriptSnippet(video.transcriptionText),
    [video.transcriptionText],
  );

  /**
   * In select mode the whole card is the hit target: a click anywhere that
   * isn't one of the card's own actions toggles the pick, so selecting doesn't
   * demand the small corner marker. Actions keep to themselves — anything
   * rendered as a button/link/input, plus the regions marked
   * `data-card-interactive` (thumbnail, kebab, workflow dropdowns, download
   * badges), swallows the click before it means "select".
   */
  const handleCardClick = (e: ReactMouseEvent) => {
    if (!selectable || !selectableId) return;
    if (!(e.target instanceof Element)) return;
    if (e.target.closest('button, a, input, [data-card-interactive]')) return;
    onToggleSelect?.(selectableId);
  };

  /**
   * Inline title rename (PLAT-11136), the same commit the kebab's Rename modal
   * makes. `pendingTitle` carries the new name over the save's round trip so
   * the card doesn't flash back to the old one; the store's own value takes
   * over once it lands, and a failed save reverts to it.
   *
   * The field reopens the moment it commits, so a second rename can start
   * while the first is still in flight. Only the newest one owns the pending
   * title — an earlier request settling later would otherwise hand the card
   * back to the stored name mid-save.
   */
  const renameClip = async (title: string) => {
    if (!title || !video.id || isEditingBlocked) return;
    const requestId = ++renameRequestRef.current;
    setPendingTitle(title);
    try {
      await videoCreator.renameVideo(video.id, title);
    } catch (e) {
      logger.error('Failed to rename clip', extractErrorInfo(e));
    } finally {
      if (renameRequestRef.current === requestId) setPendingTitle(null);
    }
  };

  return (
    <Container ref={cardRef} data-testid={`video-clip-card-${video.id}`}>
      <Card
        $isActive={isActiveClip}
        $isSelected={selectable && selected}
        $isHighlighted={isHighlighted}
        data-saving={isSaving || undefined}
        onPointerEnter={() => prefetch(video)}
        onClick={handleCardClick}
      >
        <TopActions>
          <TopActionsLeft>
            {video.clipJson?.impactScore && (
              <ImpactScoreBadgeWrapper>
                <ImpactScoreBadge
                  impactScore={video.clipJson.impactScore}
                  id={`clip-impact-score-${video.id}`}
                />
              </ImpactScoreBadgeWrapper>
            )}
            <ClipTypeBadge
              $color={isReelCard ? 'purple' : 'blue'}
              data-testid={`clip-type-badge-${video.id}`}
              // The identity badge is where "Reel" is said, so it is where the
              // answer to "what is in it" belongs — the Reel Editor's own
              // summary line, one string rather than the rich Combined Clips
              // panel this used to open: the question is what the reel holds,
              // and the line already answers it.
              {...(reelSummary && {
                'data-tooltip-id': `reel-summary-tooltip-${video.id}`,
                'data-tooltip-content': reelSummary,
              })}
            >
              {isReelCard ? 'Reel' : 'Clip'}
            </ClipTypeBadge>
            {reelSummary && (
              <Tooltip
                id={`reel-summary-tooltip-${video.id}`}
                style={BADGE_TOOLTIP_STYLE}
              />
            )}
            {/* Mounted only with something to say (PLAT-11249): the hidden
                placeholder that used to hold card heights level left a
                pill-sized hole between the badges, and the identity badge \u2014
                on every card now \u2014 holds the row's height instead. */}
            {themeTag && (
              <ThemeTag
                $color={themeTag.color}
                data-tooltip-id={`theme-tooltip-${video.id}`}
                data-tooltip-content="-"
              >
                {themeTag.label}
              </ThemeTag>
            )}
            {clipTag && (
              <Tooltip
                id={`theme-tooltip-${video.id}`}
                style={DARK_TOOLTIP_STYLE}
                opacity={1}
                render={() => {
                  const entries = buildClipTooltipEntries(video.clipJson);
                  const isReel = video.clipJson?.isReel;
                  // A reel is never "Custom Prompt", whatever its first clip
                  // was — it was not produced from a prompt.
                  const isCustom =
                    !isReel && video.clipJson?.theme === 'Custom';
                  const title = isReel ? 'Combined Clips' : undefined;
                  // The reel overlay's substance: the clips it is made of,
                  // in play order, with the count across the title row.
                  const rows = isReel ? reelClipRows(video.clipJson) : [];
                  // Always show tooltip for custom/reel/custom-prompt even with no entries
                  if (entries.length === 0 && !title && !isCustom) return null;
                  // Fallback: show type label when no prompt text available
                  if (isCustom && entries.length === 0) {
                    entries.push({ label: 'Type', value: 'Custom Prompt' });
                  }
                  return (
                    <ClipContextTooltip
                      entries={entries}
                      title={title}
                      titleValue={
                        rows.length
                          ? `${rows.length} ${rows.length === 1 ? 'Clip' : 'Clips'}`
                          : undefined
                      }
                      rows={rows}
                    />
                  );
                }}
              />
            )}
            {!isSaving && isNew && <NewBadge>New</NewBadge>}
          </TopActionsLeft>
          <TopActionsRight>
            {commentCount > 0 && (
              <>
                <CommentCountBadge
                  type="button"
                  data-testid="clip-comment-count"
                  aria-label={`View ${commentCount} ${
                    commentCount === 1 ? 'comment' : 'comments'
                  }`}
                  data-tooltip-id={`comment-tooltip-${video.id}`}
                  onClick={e => {
                    // The badge opens the clip straight to its conversation;
                    // don't let the card's own click handlers double-fire.
                    e.stopPropagation();
                    contentStudioUI.requestClipPreviewTab('comments');
                    open(video, false);
                  }}
                >
                  <CommentsIcon
                    width="16"
                    height="16"
                    strokeColor="var(--accent)"
                  />
                  <span>{commentCount}</span>
                </CommentCountBadge>
                {latestComment && (
                  <Tooltip
                    id={`comment-tooltip-${video.id}`}
                    place="bottom"
                    noArrow
                    style={BADGE_TOOLTIP_STYLE}
                    opacity={1}
                    render={() => {
                      const summary = threadSummaryLabel(commentThreads);
                      return (
                        <CommentTooltip>
                          {summary && (
                            <CommentTooltipStatus
                              $allResolved={commentThreads?.open === 0}
                            >
                              {summary}
                            </CommentTooltipStatus>
                          )}
                          <CommentTooltipHeader>
                            <CommentTooltipAuthor>
                              {latestComment.authorName}
                            </CommentTooltipAuthor>
                            <CommentTooltipTime>
                              {formatRelativeTime(latestComment.createdAt)}
                            </CommentTooltipTime>
                          </CommentTooltipHeader>
                          <CommentTooltipBody>
                            {/* Flatten `@[Name](id)` mention tokens to plain
                                `@Name` — this preview renders the stored body
                                verbatim. */}
                            {stripMentionTokens(latestComment.body)}
                          </CommentTooltipBody>
                        </CommentTooltip>
                      );
                    }}
                  />
                )}
              </>
            )}
            {!enableTranslationReviewerMode && (
              <Kebab
                ref={renameRef}
                data-card-interactive=""
                onClick={() =>
                  !isSaving && toggleRenameDropdown(!openRenameDropdown)
                }
                $disabled={isSaving}
              >
                <MoreVertIcon />
                <KebabMenu $visible={openRenameDropdown}>
                  {dimensions.map(format => {
                    if (!format.isPublished) {
                      return null;
                    }
                    return (
                      <KebabMenuItem
                        key={format.aspectRatio}
                        onClick={async e => {
                          await handleDownloadFormat(e, format);
                          toggleRenameDropdown(false);
                        }}
                      >
                        <DownloadIcon
                          width="14"
                          height="14"
                          strokeColor="currentColor"
                        />
                        <span>{downloadLabel(format)}</span>
                      </KebabMenuItem>
                    );
                  })}
                  <KebabMenuItem
                    onClick={() => {
                      toggleRenameDropdown(false);
                      setExportOpen(true);
                    }}
                  >
                    <ShareIcon
                      width="14"
                      height="14"
                      strokeColor="currentColor"
                    />
                    <span>Publish...</span>
                  </KebabMenuItem>
                  <KebabMenuItem
                    onClick={() => toggleContentToEditModal({ type: 'rename' })}
                  >
                    <PencilIcon
                      width="14"
                      height="14"
                      strokeColor="currentColor"
                    />
                    <span>Rename</span>
                  </KebabMenuItem>
                  <KebabMenuItem
                    onClick={() =>
                      toggleContentToEditModal({ type: 'save_as' })
                    }
                  >
                    <CopyIcon
                      width="14"
                      height="14"
                      strokeColor="currentColor"
                    />
                    <span>Save As</span>
                  </KebabMenuItem>
                  <KebabMenuItemDanger
                    onClick={() => {
                      toggleRenameDropdown(false);
                      setShowDeleteConfirm(true);
                    }}
                  >
                    <RemoveIcon
                      width="14"
                      height="14"
                      strokeColor="currentColor"
                    />
                    <span>Delete Clip</span>
                  </KebabMenuItemDanger>
                </KebabMenu>
              </Kebab>
            )}
            {exportOpen && (
              <ExportMenu
                open
                onClose={() => setExportOpen(false)}
                triggerRef={renameRef}
                mainClip={video as unknown as Video}
              />
            )}
          </TopActionsRight>
        </TopActions>
        <ThumbnailWrapper
          data-testid={`clip-thumbnail-${video.id}`}
          data-card-interactive=""
          onClick={() => {
            if (isEditingBlocked) return;
            open(video, true);
          }}
          style={isEditingBlocked ? { cursor: 'not-allowed' } : undefined}
        >
          {displayedThumbnailUrl && (
            <Thumbnail
              src={displayedThumbnailUrl}
              alt="thumbnail"
              fill={fillImage}
              objectFit={aspectRatio === '9:16' ? 'contain' : 'cover'}
            />
          )}
          <HoverOverlay className="hover-overlay">
            <SeeDetailsButton
              type="button"
              disabled={isEditingBlocked}
              onClick={e => {
                e.stopPropagation();
                if (isEditingBlocked) return;
                open(video, true);
              }}
            >
              <span>{video.clipJson?.isReel ? 'Edit Reel' : 'Edit Clip'}</span>
              <ArrowRightIcon />
            </SeeDetailsButton>
          </HoverOverlay>
          {enableSharing && (
            <StatsOverlay>
              <SocialStats
                type="video"
                id={video.id}
                allReferencingSharedContents={
                  video._allReferencingSharedContents
                }
                layout="wide"
              />
            </StatsOverlay>
          )}
          {duration.duration != null &&
            duration.formattedDuration !== '--:--' && (
              <DurationOverlayBadge>
                {duration.formattedDuration}
              </DurationOverlayBadge>
            )}
          {/* The pick marker, on the frame's top-left corner where it has
              always lived (PLAT-11049 DS3).

              The box and its pad are the whole control (PLAT-11746). The rest
              of the frame opens the preview, so what a click on the video does
              is what it looks like it will do — the card body below is still
              the other way to pick, for picking a run of clips quickly. */}
          {selectable && selectableId && (
            <SelectToggle
              className="select-toggle"
              $revealed={selected || selectionActive}
              type="button"
              role="checkbox"
              aria-checked={selected}
              aria-label={`Select ${video.title ?? 'clip'}`}
              data-testid={`clip-select-${selectableId}`}
              onClick={e => {
                e.stopPropagation();
                onToggleSelect?.(selectableId);
              }}
            >
              {/* The box the card carried before the marker moved to the
                  header, with the pick's position inside it instead of a tick
                  (PLAT-11092 DS2a). The wrapper lifts on hover anywhere in the
                  zone, so the whole region reads as one control. */}
              <SelectDisc>
                <SelectionOrderBadge
                  variant="frame"
                  position={selected ? selectionOrder : null}
                />
              </SelectDisc>
            </SelectToggle>
          )}
          {/* Who is heard in the clip, on the opposite bottom corner from the
              length — the two facts about a clip you can read off the frame
              without opening it. Absent where nobody in it was ever identified,
              rather than an anonymous placeholder, which is the rule the
              Section rows and the Add Clips picker already follow.
              `pointer-events: none` so it never intercepts the frame's click. */}
          {cardSpeakers.length > 0 && (
            <SpeakerOverlay data-testid={`clip-speakers-${video.id}`}>
              <SpeakerFacePile
                speakers={cardSpeakers}
                size={20}
                tooltipId={CLIP_CARD_SPEAKER_TOOLTIP_ID}
                // No ring: over a video frame the default background-coloured
                // gap reads as a black outline drawn around each face.
                ringColor="transparent"
              />
            </SpeakerOverlay>
          )}
        </ThumbnailWrapper>

        <TitleContainer>
          {/* Renaming is authoring, so a translation reviewer reads the title
              instead of editing it — the same exclusion that takes away the
              kebab above and the reel/delete markers on the frame. */}
          <InlineRenameField
            value={pendingTitle ?? video.title ?? ''}
            placeholder="Untitled Clip"
            ariaLabel={`Rename ${video.title ?? 'clip'}`}
            testId={`clip-title-${video.id}`}
            onCommit={renameClip}
            readOnly={enableTranslationReviewerMode}
          />
        </TitleContainer>

        {/* Transcript opener; hovering it reveals a longer excerpt so the clip
            can be judged without opening the preview. The tooltip anchors on
            this line only — never the whole card — so it doesn't fight the
            thumbnail's Edit Clip overlay. */}
        <TranscriptSnippet
          data-testid={`clip-transcript-snippet-${video.id}`}
          {...(snippet
            ? { 'data-tooltip-id': `transcript-tooltip-${video.id}` }
            : { 'aria-hidden': true })}
        >
          {snippet}
        </TranscriptSnippet>
        {snippet &&
          // Portaled to <body>: the Card's overflow:hidden would clip an
          // in-card tooltip taller than the card (TranslationToggle recipe).
          createPortal(
            <Tooltip
              id={`transcript-tooltip-${video.id}`}
              place="bottom-start"
              noArrow
              style={DARK_TOOLTIP_STYLE}
              opacity={1}
              render={() => (
                <TranscriptExcerptPanel
                  text={video.transcriptionText ?? ''}
                  title="Transcript"
                />
              )}
            />,
            document.body,
          )}

        <VideoBadgeRow>
          {isSaving && (
            <span data-testid="clip-saving-badge">
              <AutosaveStatusIndicator status="saving" />
            </span>
          )}
          <DimensionsContainer>
            {dimensions.map(format => {
              const isDownloadable = format.isPublished && !isPublishing;
              return (
                <DimensionBadge
                  inactive={!format.isPublished}
                  publishing={isPublishing}
                  $downloadable={isDownloadable}
                  key={format.aspectRatio}
                  {...(isDownloadable && { 'data-card-interactive': '' })}
                  data-tooltip-id={`dimension-${video.id}`}
                  data-tooltip-content={
                    isPublishing
                      ? 'Publishing...'
                      : isDownloadable
                        ? downloadLabel(format)
                        : 'Draft'
                  }
                  onClick={
                    isDownloadable
                      ? e => void handleDownloadFormat(e, format)
                      : undefined
                  }
                >
                  <span className="badge-label">{format.aspectRatio}</span>
                  {isDownloadable && (
                    <span className="badge-download">
                      <DownloadIcon
                        width="12"
                        height="12"
                        strokeColor="currentColor"
                      />
                    </span>
                  )}
                </DimensionBadge>
              );
            })}
            <Tooltip
              id={`dimension-${video.id}`}
              place="bottom"
              noArrow
              opacity={1}
              style={BADGE_TOOLTIP_STYLE}
            />
          </DimensionsContainer>
          {isPublishing ? (
            <PublishingIndicator data-testid={`clip-publishing-${video.id}`}>
              <PublishingSpinner />
              <span>Publishing...</span>
            </PublishingIndicator>
          ) : (
            <SocialIconsWrapper>
              {enableSharing && (
                <SocialShareStatus
                  hideItems={
                    dimensions.length > 2 && countOfTrue > 5
                      ? { x: true, twitter: true }
                      : undefined
                  }
                  showOnlyShared={true}
                  sharedContents={video._allReferencingSharedContents}
                  padding="0"
                />
              )}
            </SocialIconsWrapper>
          )}
        </VideoBadgeRow>

        {contentStudioEnableClipStatusAssignee && (
          <ClipWorkflowRow data-card-interactive="">
            <ClipCardSelect
              ariaLabel="Status"
              value={video.reviewStatus || DEFAULT_CLIP_REVIEW_STATUS}
              options={CLIP_STATUS_OPTIONS}
              onSelect={value =>
                videoCreator.updateReviewStatus(
                  value as ClipReviewStatus,
                  video.id,
                )
              }
            />
            <ClipCardSelect
              ariaLabel="Assignee"
              value={video.assignee}
              placeholder="Unassigned"
              options={assigneeOptions}
              emptyText="No showcase members"
              onSelect={value =>
                videoCreator.updateClipAssignee(value, video.id)
              }
            />
          </ClipWorkflowRow>
        )}

        <BottomRow>
          {renderPublishInfo()}
          {appEnableApprovedButton && (
            // The flag renders as a clickable div, so it marks itself out of
            // the card's select-anywhere click here.
            <span data-card-interactive="">
              <ClientReadyFlag iconOnly video={video} />
            </span>
          )}
        </BottomRow>
      </Card>

      {contentToEdit && (
        <Modal
          isOpen={contentToEdit !== null}
          closeModal={() => toggleContentToEditModal(null)}
        >
          <CopyContentModal
            name={
              contentToEdit.type === 'save_as'
                ? 'Copy of ' + (video.title ?? '')
                : (video.title! ?? '')
            }
            tag="Clip"
            type={contentToEdit.type}
            onCancel={() => toggleContentToEditModal(null)}
            onSave={async title => {
              if (contentToEdit?.type === 'rename') {
                toggleContentToEditModal(null);
                setLoadingSpinnerText('Renaming video');
                await videoCreator.renameVideo(video.id!, title);
                setLoadingSpinnerText(undefined);
              } else if (contentToEdit?.type === 'save_as') {
                toggleContentToEditModal(null);
                await copyClipWithOptimisticUI(videoCreator, video, title);
              }
            }}
          />
        </Modal>
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          closeModal={() => setShowDeleteConfirm(false)}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            setShowDeleteConfirm(false);
            setLoadingSpinnerText('Deleting video');
            await deleteVideoClip();
            setLoadingSpinnerText(undefined);
          }}
          confirmText="Delete"
          cancelText="Cancel"
          title="Delete this clip?"
          message="This cannot be undone."
        />
      )}

      {loadingSpinnerText && (
        <SpinningLoading text={loadingSpinnerText} spinnerSize="32px" />
      )}
    </Container>
  );
});

export default VideoClipCard;

const Container = styled.div`
  display: flex;
  position: relative; /* containing block for the rename/delete overlay */
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 280px;
  max-width: 392px;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const highlightFade = keyframes`
  0% {
    border-color: var(--primary);
  }
  70% {
    border-color: var(--primary);
  }
  100% {
    border-color: transparent;
  }
`;

const Card = styled.div<{
  $isActive?: boolean;
  $isHighlighted?: boolean;
  $isSelected?: boolean;
}>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: var(--surface);
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition:
    box-shadow var(--transition-normal) ease,
    border-color var(--transition-normal) ease;
  /* Active (open in the preview) keeps the gold ring; a reel pick shows the
     Clip Producer's green selected border. 1.5px matches the AIClip card. */
  border: 1.5px solid
    ${props =>
      props.$isActive
        ? 'var(--accent)'
        : props.$isSelected
          ? 'var(--primary)'
          : 'transparent'};
  ${props =>
    props.$isHighlighted &&
    css`
      animation: ${highlightFade} ${HIGHLIGHT_DURATION_MS}ms ease-out forwards;
    `}

  /* Hover lifts the card a full step (the grid's cards sit shadowless, so
     elevation-md is the contrast one card needs) — no border recolor. */
  &:hover {
    box-shadow: var(--elevation-md);
  }

  /* Hovering anywhere on the card raises the pick box, the same way the overlay
     above is raised. A class rather than the styled-component interpolation
     because SelectToggle is declared below this one. */
  &:hover .select-toggle {
    opacity: 1;
  }

  &:hover,
  &:focus-within {
    .social-stats {
      opacity: 0;
    }
    .actions-menu {
      opacity: 1;
    }
  }
`;

const VideoBadgeRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  gap: 8px;
  min-height: 28px;
  padding: 4px 12px 0;
`;

const DimensionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DimensionBadge = styled.div<{
  inactive?: boolean;
  publishing?: boolean;
  $downloadable?: boolean;
}>`
  ${dimensionBadgeBase}
  position: relative;
  display: flex;
  font-weight: 500;
  transition: background-color var(--transition-fast) ease;
  ${props => dimensionBadgeStateColors(props)}

  .badge-download {
    display: none;
    position: absolute;
    inset: 0;
    align-items: center;
    justify-content: center;
  }

  ${props =>
    props.$downloadable &&
    css`
      cursor: pointer;

      &:hover {
        background-color: color-mix(
          in srgb,
          var(--primary) 85%,
          var(--background)
        );

        .badge-label {
          visibility: hidden;
        }

        .badge-download {
          display: flex;
        }
      }
    `}
`;

const SocialIconsWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const PublishingIndicator = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background-color: var(--accent);
  color: var(--background);
  font-size: 10px;
  font-weight: 600;
  line-height: 120%;
  white-space: nowrap;
`;

const PublishingSpinner = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1.5px solid color-mix(in srgb, currentColor 30%, transparent);
  border-top-color: currentColor;
  animation: clip-card-publishing-spin 0.8s linear infinite;

  @keyframes clip-card-publishing-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ImpactScoreBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Kebab = styled.div<{ $disabled?: boolean }>`
  position: relative;
  margin-left: 2px;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 4px;
  border-radius: 4px;
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: background var(--transition-fast);
  color: var(--text-muted);
  &:hover {
    background: var(--sidebar-hover);
    color: ${props =>
      props.$disabled ? 'var(--text-muted)' : 'var(--text-secondary)'};
  }
`;

const KebabMenu = styled.div<{ $visible: boolean }>`
  display: ${props => (props.$visible ? 'flex' : 'none')};
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 10001;
  flex-direction: column;
  min-width: 160px;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px;
  box-shadow: var(--elevation-md);
`;

const KebabMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--sidebar-hover);
    color: var(--foreground);
  }

  svg {
    flex-shrink: 0;
  }
`;

const KebabMenuItemDanger = styled(KebabMenuItem)`
  &:hover {
    background: rgba(239, 68, 68, 0.15);
    color: var(--destructive);
  }
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
`;

const TopActionsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TopActionsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* The title is inline-renameable, so the row is inset by the rename field's own
   border + padding (1px + 5px): the title text keeps the card's 12px gutter in
   both the resting and the open state, lining up with the transcript opener
   below it. */
const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 12px 6px 4px;
  min-width: 0;

  /* The card title's own weight; the field otherwise carries the shared row
     type. Keyed on the name itself rather than the element around it, which is
     a button or a span depending on whether the host allows a rename. */
  [data-rename-text] {
    font-weight: var(--font-weight-medium);
  }

  /* The pencil belongs to the title row, not to the text itself (the
     selectable-item pattern the Reel Editor rows use). */
  &:hover {
    ${PENCIL_REVEAL}: 1;
  }
`;

/* Two-line transcript opener under the title (design-system "row summary
   sub-line"); the slot reserves its height even when a clip has no transcript
   so cards stay aligned (PLAT-10867). */
const TranscriptSnippet = styled.div`
  padding: 0 12px;
  /* Spacing to the badges must be margin, not padding: overflow:hidden clips
     at the padding edge, so clamped-away lines would paint into bottom
     padding. 20px + VideoBadgeRow's 4px top padding = 24px. */
  margin-bottom: 20px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(var(--leading-text-xs) * 2);
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background-color: var(--gray-500);
  overflow: hidden;
  cursor: pointer;

  /* Edit Clip belongs to the thumbnail, not the card: revealed from anywhere on
     the card it read as the title's affordance, and it covered the one place a
     creator would go to rename the clip (PLAT-11136). */
  &:hover .hover-overlay {
    opacity: 1;
  }
`;

const Thumbnail = styled.img<{
  fill: boolean;
  objectFit: 'cover' | 'contain';
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: ${props => props.objectFit};
  object-position: center;
  background-color: var(--background);
`;

// Clickable: opens the clip-preview modal on the Comments tab.
const CommentCountBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs, 4px);
  padding: var(--spacing-xs, 4px);
  border: none;
  background: none;
  color: var(--accent);
  font-size: var(--text-xs, 12px);
  font-weight: var(--font-weight-normal);
  line-height: 120%;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity var(--transition-fast, 0.15s) ease;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
    border-radius: var(--radius-sm, 6px);
  }

  svg {
    flex-shrink: 0;
  }
`;

/* Panel chrome (background, border, shadow, padding) comes from
   BADGE_TOOLTIP_STYLE on the Tooltip wrapper, matching the dimension tooltip. */
const CommentTooltip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 260px;
`;

// "2 open · 1 resolved" summary; green when every thread is resolved so a
// fully-handled clip reads as done at a glance.
const CommentTooltipStatus = styled.div<{ $allResolved?: boolean }>`
  color: ${props =>
    props.$allResolved ? 'var(--primary)' : 'var(--text-muted)'};
  font-weight: var(--font-weight-normal);
`;

const CommentTooltipHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const CommentTooltipAuthor = styled.span`
  font-weight: var(--font-weight-medium);
  color: var(--popover-foreground);
`;

const CommentTooltipTime = styled.span`
  color: var(--text-muted);
  white-space: nowrap;
`;

const CommentTooltipBody = styled.div`
  color: var(--popover-foreground);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NewBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background-color: var(--surface-elevated);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  white-space: nowrap;
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-normal) ease;
`;

/* The pick control on the thumbnail's top-left corner, sitting over the hover
   overlay so it stays operable while Edit Clip is showing.

   It is the box plus its padding and nothing more (PLAT-11746). It used to be
   the frame's whole top-left quadrant, so that picking a run of clips was a
   comfortable target — but a transparent region that large reads as part of the
   video, and a click there selected the clip when it plainly looked like it
   would open it. Everything on the frame outside this box opens the preview. */
const SelectToggle = styled.button<{ $revealed?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  /* The pad is the whole hit area now, so it is what keeps the 22px box a
     comfortable target: 8px on each side brings the control to 38px. */
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;

  /* Hidden until it is wanted: an empty box drawn over every thumbnail reads as
     a mark on the video rather than a control. Three things reveal it — hover
     (the class rule on Card, the same way the hover overlay and the actions
     menu work), this card being picked, and any card being picked. That last
     one is what makes a run of picks comfortable: once a selection exists, the
     boxes stay up across the grid so the next pick is one click rather than
     hover-then-click.

     Opacity rather than mounting, so the box stays clickable while invisible:
     a click on the hidden box picks the clip, which is how a first pick can be
     made without waiting for hover to resolve. */
  opacity: ${props => (props.$revealed ? 1 : 0)};
  transition: opacity var(--transition-fast) ease;

  &:focus-visible {
    opacity: 1;
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
`;

/* Carries the control's hover: the feedback comes from the box rather than from
   filling the pad around it, which is transparent over the video. Brightening
   the border is enough — the box already has a fill of its own. */
const SelectDisc = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--spacing-2xs);
  transition: transform var(--transition-fast);

  ${SelectToggle}:hover & {
    transform: scale(1.08);
  }
`;

/* The face pile on the frame's bottom-left, mirroring the duration badge
   opposite it. Never a click target — the frame underneath it opens the
   preview — so only the avatars themselves take hover, for their names. */
const SpeakerOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const SeeDetailsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const StatsOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  z-index: 1;
  padding: 0 16px;
  pointer-events: none;
  > div {
    margin: 0;
  }
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 16px;
  > div:first-child {
    flex: 1;
    /* Reserve a fixed one-line slot so published, draft, and no-info cards
       all end up the same height (PLAT-10867); min-width: 0 lets the meta
       text ellipsize instead of stretching the card. */
    min-height: calc(11px * 1.4);
    min-width: 0;
    margin-top: 0;
  }
`;

const ClipWorkflowRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 12px 12px 0;
`;

/* Same typography as LastPublishedInfo's PublishInfo so draft and published
   metadata lines render identically; single line, editor name (the tail)
   truncates with an ellipsis (PLAT-10867). */
const DraftInfo = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  font-weight: var(--font-weight-normal);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ThemeTag = styled.div<{
  $color?: BadgeColor | 'orange';
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background-color: ${props => `var(--badge-${props.$color || 'blue'}-bg)`};
  color: ${props => `var(--badge-${props.$color || 'blue'}-text)`};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  white-space: nowrap;
  width: fit-content;
`;
